import Close from "@mui/icons-material/Close";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useAsyncStateSupabaseCallback } from "../../utils/hooks/use-async-state-callback";
import { FolderParentPicker, useUpdateFolderParentCallback } from "./components/folder-parent";
import { SupabaseFolderService } from "./server/folder-service";
import { ROOT_ID, folderNameFromId } from "./utils";

import type { UUID } from "../../utils/uuid";
import type { Folder } from "./server/folder-service";
import type { UiFolderId } from "./utils";
import type { FC } from "react";

export interface MoveFolderData {
  name: string;
  folderId: UUID;
}

interface MoveFolderFormData {
  selectedParentFolderId: UiFolderId;
  showPicker: boolean;
  showNewFolderField: boolean;
  newFolderName: string;
}

export const MoveFolderModal: FC<{
  open: boolean;
  onClose: () => void;
  reloadFolders: () => void;
  name: string;
  folderId: UUID;
  folders: Folder[] | null;
  loading: boolean;
  error: Error | null;
}> = ({ open, onClose, reloadFolders, name, folderId, folders, loading, error: foldersError }) => {
  const updateFolderParent = useUpdateFolderParentCallback();

  // Local folders state to avoid full page refresh when creating folders
  const [localFolders, setLocalFolders] = useState<Folder[] | null>(folders);

  // Track if any folders were created during this modal session
  const [foldersCreated, setFoldersCreated] = useState(false);

  // Sync local folders with props
  const [prevFolders, setPrevFolders] = useState(folders);
  if (folders !== prevFolders) {
    setPrevFolders(folders);
    setLocalFolders(folders);
  }

  // Reset foldersCreated flag when modal opens
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setFoldersCreated(false);
  }

  // Get the current parent folder ID
  const currentFolder = localFolders?.find((f) => f.id === folderId);
  const currentParentFolderId: UiFolderId = currentFolder?.parent_folder_id ?? ROOT_ID;

  const methods = useForm<MoveFolderFormData>({
    defaultValues: {
      selectedParentFolderId: currentParentFolderId,
      showPicker: false,
      showNewFolderField: false,
      newFolderName: "",
    },
  });

  const { control, reset, setValue, handleSubmit } = methods;

  const [selectedParentFolderId, showPicker, showNewFolderField, newFolderName] = useWatch({
    control,
    name: ["selectedParentFolderId", "showPicker", "showNewFolderField", "newFolderName"],
  });

  useEffect(() => {
    if (open) {
      reset();
      setValue("selectedParentFolderId", currentParentFolderId);
    }
  }, [open, currentParentFolderId, reset, setValue]);

  const hasPendingMove = selectedParentFolderId !== currentParentFolderId;
  const normalizedNewFolderName = newFolderName.trim();
  const selectedFolderLabel =
    folderNameFromId(selectedParentFolderId, localFolders) ?? "Unknown folder";

  // Check for duplicate folder name in the selected parent folder
  const hasDuplicateFolderName = useMemo(() => {
    if (!normalizedNewFolderName || !localFolders) return false;
    const parentFolderId = selectedParentFolderId === ROOT_ID ? null : selectedParentFolderId;
    return localFolders.some(
      (folder) =>
        folder.parent_folder_id === parentFolderId &&
        folder.name.trim() === normalizedNewFolderName,
    );
  }, [localFolders, normalizedNewFolderName, selectedParentFolderId]);

  const { loading: savingNewFolder, load: saveNewFolder } = useAsyncStateSupabaseCallback(
    async (
      { supabase },
      { folderName, parentFolderId }: { folderName: string; parentFolderId: UUID | null },
    ) => {
      const service = new SupabaseFolderService(supabase);
      const { data, error: newFolderError } = await service.insert({
        parent_folder_id: parentFolderId,
        name: folderName,
      });
      if (newFolderError) throw newFolderError;
      return data;
    },
    [],
  );

  const { loading: moving, load: executeMove } = useAsyncStateSupabaseCallback(
    async (_, { targetFolderId }: { targetFolderId: UUID | null }) => {
      const result = await updateFolderParent(folderId, targetFolderId);
      if (!result?.success) {
        throw new Error(result?.error ?? "Folder move failed");
      }
      return result;
    },
    [folderId, updateFolderParent],
  );

  const handleCreateFolder = useCallback(async (): Promise<void> => {
    if (normalizedNewFolderName.length === 0 || hasDuplicateFolderName) return;

    const parentFolderId = selectedParentFolderId === ROOT_ID ? null : selectedParentFolderId;

    try {
      const createdFolder = await saveNewFolder({
        folderName: normalizedNewFolderName,
        parentFolderId,
      });

      if (!createdFolder) return;

      toast.success(`Folder '${createdFolder.name}' created`);

      // Clear the new folder input
      setValue("newFolderName", "");
      setValue("showNewFolderField", false);

      // Update local folders state to include the new folder (no full page refresh)
      setLocalFolders((prev) => (prev ? [...prev, createdFolder] : [createdFolder]));
      setFoldersCreated(true);

      // Auto-select the newly created folder as destination
      setValue("selectedParentFolderId", createdFolder.id);
    } catch (error) {
      console.error(error);
      if (error instanceof Error && error.message.includes("folder_unique_name")) {
        toast.error("Folder name must be unique");
      } else {
        toast.error("Error when creating folder");
      }
    }
  }, [
    normalizedNewFolderName,
    hasDuplicateFolderName,
    selectedParentFolderId,
    saveNewFolder,
    setValue,
  ]);

  // Handle move only (no folder creation in submit)
  const onSubmit = useCallback(
    async (data: MoveFolderFormData) => {
      try {
        // Only perform move if destination is different from current
        if (data.selectedParentFolderId === currentParentFolderId) return;

        const nextParentFolderId =
          data.selectedParentFolderId === ROOT_ID ? null : data.selectedParentFolderId;
        await executeMove({ targetFolderId: nextParentFolderId });

        const destinationName = folderNameFromId(data.selectedParentFolderId, localFolders);
        toast.custom(<Alert severity="success">{`Folder moved to '${destinationName}'`}</Alert>, {
          duration: 3000,
        });
        // Reload parent folders after successful move
        reloadFolders();
        onClose();
      } catch (error) {
        console.error(error);
        if (error instanceof Error && error.message.includes("folder_unique_name")) {
          toast.error("Folder name must be unique");
        } else if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error("Error when moving folder");
        }
      }
    },
    [currentParentFolderId, executeMove, localFolders, onClose, reloadFolders],
  );

  const handleSelectParentFolder = (parentFolderId: UiFolderId): void => {
    setValue("selectedParentFolderId", parentFolderId);
  };

  // Handle cancel - reload folders if any were created
  const handleCancel = useCallback(() => {
    if (foldersCreated) {
      reloadFolders();
    }
    onClose();
  }, [foldersCreated, onClose, reloadFolders]);

  // Move button is enabled only when destination differs from current
  const canMove = hasPendingMove;

  const isSubmitting = moving;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <FormProvider {...methods}>
        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
          <DialogTitle>{`Move "${name}"`}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <Box>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
                  Current location
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FolderOutlined fontSize="small" sx={{ color: "text.secondary" }} />
                  {loading ? (
                    <Skeleton variant="text" width={160} />
                  ) : (
                    <Typography variant="body2" noWrap>
                      {folderNameFromId(currentParentFolderId, localFolders) ?? "Unknown folder"}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Destination selection dropdown-style display */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Move to:
                  </Typography>
                  {loading ? (
                    <Skeleton variant="text" width={160} />
                  ) : (
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                      {selectedFolderLabel}
                    </Typography>
                  )}
                </Box>
                <Button
                  onClick={() => setValue("showPicker", !showPicker)}
                  variant="text"
                  size="small"
                  sx={{ textTransform: "none", fontWeight: 500, px: 0, whiteSpace: "nowrap" }}
                  data-analytics-id="move-folder-toggle-picker"
                >
                  {showPicker ? "Hide folders" : "Select folder"}
                </Button>
              </Box>

              {showPicker && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1,
                    maxHeight: 300,
                    overflowY: "auto",
                  }}
                >
                  <FolderParentPicker
                    folderId={folderId}
                    selectedParentFolderId={selectedParentFolderId}
                    onSelectParentFolder={handleSelectParentFolder}
                    folders={localFolders ?? null}
                    loading={loading}
                    error={foldersError ?? null}
                  />
                </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Button
                  onClick={() => {
                    setValue("newFolderName", "");
                    setValue("showNewFolderField", true);
                  }}
                  variant="text"
                  size="small"
                  data-analytics-id="move-folder-create-new-folder"
                  disabled={showNewFolderField}
                  sx={{
                    p: 0,
                    minHeight: "auto",
                    textTransform: "none",
                    lineHeight: 1.5,
                  }}
                >
                  <Typography variant="body2">
                    {`Create new folder in '${selectedFolderLabel}'`}
                  </Typography>
                </Button>

                {showNewFolderField && (
                  <Tooltip title="Cancel new folder creation">
                    <IconButton
                      size="small"
                      data-analytics-id="move-folder-create-new-folder-cancel"
                      onClick={() => {
                        setValue("showNewFolderField", false);
                        setValue("newFolderName", "");
                      }}
                      aria-label="Cancel new folder creation"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {showNewFolderField && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Controller
                    name="newFolderName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="New folder name"
                        error={hasDuplicateFolderName}
                        helperText={
                          hasDuplicateFolderName
                            ? `A folder with this name already exists in '${selectedFolderLabel}'`
                            : null
                        }
                      />
                    )}
                  />
                  <Button
                    onClick={() => void handleCreateFolder()}
                    variant="contained"
                    data-analytics-id="move-folder-create-new-folder-confirm"
                    disabled={
                      savingNewFolder ||
                      normalizedNewFolderName.length === 0 ||
                      hasDuplicateFolderName
                    }
                    sx={{ minWidth: "auto", whiteSpace: "nowrap" }}
                  >
                    {savingNewFolder ? "Creating..." : "Create"}
                  </Button>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Stack
              direction="row"
              spacing={2}
              sx={{ width: "100%", p: 1, justifyContent: "flex-end" }}
            >
              <Button
                data-analytics-id="move-folder-cancel"
                variant="text"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                data-analytics-id="move-folder-confirm"
                variant="contained"
                type="submit"
                disabled={!canMove || isSubmitting}
              >
                {isSubmitting ? "Moving..." : "Move folder"}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
